import { format, formatDistanceToNow, parseISO, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';

// Date formatting utilities
export const formatDate = (date: string | Date, formatStr: string = 'dd/MM/yyyy'): string => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr, { locale: es });
};

export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'dd/MM/yyyy HH:mm');
};

export const formatTimeAgo = (date: string | Date): string => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: es });
};

export const getDaysUntilExpiration = (expirationDate: string | Date): number => {
  if (!expirationDate) return 0;
  const dateObj = typeof expirationDate === 'string' ? parseISO(expirationDate) : expirationDate;
  return differenceInDays(dateObj, new Date());
};

// Status utilities
export const getStatusColor = (status: string): string => {
  const statusColors: { [key: string]: string } = {
    active: '#4CAF50',
    inactive: '#9E9E9E',
    maintenance: '#FF9800',
    expired: '#F44336',
    suspended: '#FF5722',
    pending: '#FFC107',
    overdue: '#D32F2F',
    completed: '#388E3C',
    lost: '#795548',
    stolen: '#F44336',
    'payment_pending': '#FF9800',
    contacted: '#2196F3',
    declined: '#F44336',
  };
  return statusColors[status] || '#9E9E9E';
};

export const getStatusLabel = (status: string): string => {
  const statusLabels: { [key: string]: string } = {
    active: 'Activo',
    inactive: 'Inactivo',
    maintenance: 'Mantenimiento',
    expired: 'Expirado',
    suspended: 'Suspendido',
    pending: 'Pendiente',
    overdue: 'Vencido',
    completed: 'Completado',
    lost: 'Perdido',
    stolen: 'Robado',
    payment_pending: 'Pago Pendiente',
    contacted: 'Contactado',
    declined: 'Rechazado',
    cancelled: 'Cancelado',
  };
  return statusLabels[status] || status;
};

export const getRenewalPriority = (daysUntilExpiration: number): 'low' | 'medium' | 'high' | 'urgent' => {
  if (daysUntilExpiration < 0) return 'urgent'; // Overdue
  if (daysUntilExpiration <= 7) return 'high';
  if (daysUntilExpiration <= 20) return 'medium';
  return 'low';
};

export const getPriorityColor = (priority: string): string => {
  const priorityColors: { [key: string]: string } = {
    low: '#4CAF50',
    medium: '#FF9800',
    high: '#FF5722',
    urgent: '#F44336',
  };
  return priorityColors[priority] || '#9E9E9E';
};

export const getPriorityLabel = (priority: string): string => {
  const priorityLabels: { [key: string]: string } = {
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente',
  };
  return priorityLabels[priority] || priority;
};

// Data formatting utilities
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
  }).format(amount);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDataUsage = (bytes: number): string => {
  if (bytes === 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  const gb = mb / 1024;

  if (gb >= 1) {
    return `${gb.toFixed(2)} GB`;
  }
  return `${mb.toFixed(0)} MB`;
};

// Validation utilities
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[\d\s\-()]{8,}$/;
  return phoneRegex.test(phone);
};

export const isValidIMEI = (imei: string): boolean => {
  // IMEI should be 15 digits
  const imeiRegex = /^\d{15}$/;
  return imeiRegex.test(imei);
};

export const isValidICCID = (iccid: string): boolean => {
  // ICCID should be 19-20 digits
  const iccidRegex = /^\d{19,20}$/;
  return iccidRegex.test(iccid);
};

// Search utilities
export const fuzzySearch = (items: any[], searchTerm: string, searchFields: string[]): any[] => {
  if (!searchTerm) return items;

  const lowercaseSearch = searchTerm.toLowerCase();

  return items.filter(item => {
    return searchFields.some(field => {
      const value = getNestedProperty(item, field);
      return value && value.toString().toLowerCase().includes(lowercaseSearch);
    });
  });
};

export const getNestedProperty = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Array utilities
export const groupBy = <T>(array: T[], key: keyof T): { [key: string]: T[] } => {
  return array.reduce((groups, item) => {
    const group = item[key] as unknown as string;
    groups[group] = groups[group] || [];
    groups[group].push(item);
    return groups;
  }, {} as { [key: string]: T[] });
};

export const sortBy = <T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Device utilities
export const getDeviceStatusIcon = (status: string): string => {
  const statusIcons: { [key: string]: string } = {
    active: '🟢',
    inactive: '⚫',
    maintenance: '🟡',
    lost: '🔴',
    stolen: '🔴',
  };
  return statusIcons[status] || '⚫';
};

/**
 * Formatea el nombre principal del dispositivo
 * Prioridad: nombre > IMEI (nunca muestra Generic Unknown)
 * Busca en: device.name, device.traccarData.name
 */
export const getDeviceDisplayName = (device: any): string => {
  if (!device) return 'N/A';

  // Obtener el nombre de diferentes fuentes posibles
  const name = device.name || device.traccarData?.name;
  const imei = device.imei || device.uniqueId;

  // Si tiene nombre y es diferente al IMEI, usarlo
  if (name && name.trim() && name !== imei) {
    return name;
  }

  // Si no tiene nombre válido, mostrar IMEI
  if (imei) return imei;

  return 'Sin identificar';
};

/**
 * Obtiene la descripción secundaria del dispositivo
 * Muestra IMEI + marca/modelo solo si no son genéricos
 * Busca nombre en: device.name, device.traccarData.name
 */
export const getDeviceSubtitle = (device: any): string => {
  if (!device) return '';
  const parts: string[] = [];

  // Obtener valores
  const name = device.name || device.traccarData?.name;
  const imei = device.imei || device.uniqueId;
  const hasRealName = name && name.trim() && name !== imei;

  // Agregar IMEI si el nombre principal no es IMEI
  if (hasRealName && imei) {
    parts.push(`IMEI: ${imei}`);
  }

  // Agregar marca/modelo solo si no son genéricos
  const brand = device.brand || device.traccarData?.category;
  const model = device.model || device.traccarData?.model;
  const isGenericBrand = !brand || brand === 'Generic' || brand === 'N/A';
  const isGenericModel = !model || model === 'Unknown' || model === 'N/A';

  if (!isGenericBrand && !isGenericModel) {
    parts.push(`${brand} ${model}`);
  } else if (!isGenericBrand) {
    parts.push(brand);
  } else if (!isGenericModel) {
    parts.push(model);
  }

  return parts.join(' • ');
};

/**
 * Obtiene marca/modelo legible (evita Generic Unknown)
 * Busca en: device.brand/model, device.traccarData.category/model
 */
export const getDeviceBrandModel = (device: any): string => {
  if (!device) return 'N/A';
  const brand = device.brand || device.traccarData?.category;
  const model = device.model || device.traccarData?.model;
  const isGenericBrand = !brand || brand === 'Generic' || brand === 'N/A';
  const isGenericModel = !model || model === 'Unknown' || model === 'N/A';

  if (!isGenericBrand && !isGenericModel) {
    return `${brand} ${model}`;
  } else if (!isGenericBrand) {
    return brand;
  } else if (!isGenericModel) {
    return model;
  }
  // Si todo es genérico, devolver el nombre o N/A
  const name = device.name || device.traccarData?.name;
  return name || 'N/A';
};

export const calculateDeviceHealth = (device: any): 'excellent' | 'good' | 'fair' | 'poor' => {
  let score = 100;

  // Check last seen (reduce score if not seen recently)
  if (device.lastSeen) {
    const daysSinceLastSeen = getDaysUntilExpiration(device.lastSeen) * -1;
    if (daysSinceLastSeen > 7) score -= 30;
    else if (daysSinceLastSeen > 3) score -= 15;
    else if (daysSinceLastSeen > 1) score -= 5;
  } else {
    score -= 50;
  }

  // Check status
  if (device.status === 'maintenance') score -= 20;
  if (device.status === 'inactive') score -= 40;
  if (device.status === 'lost' || device.status === 'stolen') score = 0;

  // Check SIM card status
  if (device.simCard?.status === 'expired') score -= 30;
  if (device.simCard?.status === 'suspended') score -= 20;

  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
};

// Export utilities
export const downloadCSV = (data: any[], filename: string, columns: { key: string; label: string }[]) => {
  const csvContent = [
    columns.map(col => col.label).join(','),
    ...data.map(row =>
      columns.map(col => {
        const value = getNestedProperty(row, col.key);
        return `"${value || ''}"`;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Local storage utilities
export const storage = {
  get: (key: string, defaultValue: any = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },
  remove: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },
};

/**
 * Convierte horas en 24h a 12h con AM/PM. Acepta valores sueltos ('14:47' → '2:47 PM')
 * o rangos ('08:00 - 10:00' → '8:00 AM - 10:00 AM'). Deja intacto lo que no sea hora.
 * Las horas se muestran en la zona del navegador (GMT-4 en RD).
 */
export function to12h(value?: string | null): string {
  if (!value) return '';
  return String(value).replace(/(\d{1,2}):(\d{2})/g, (_m, h, mm) => {
    let hr = parseInt(h, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12;
    if (hr === 0) hr = 12;
    return `${hr}:${mm} ${ampm}`;
  });
}
